import { z } from "zod";
import { normalizeRelativePath } from "../domain/paths";

export const CURRENT_OPERATION_SCHEMA_VERSION = 1;

export const operationTypes = ["rename", "move", "duplicate", "trash", "restore"] as const;
export type OperationType = (typeof operationTypes)[number];

export const operationPhases = [
  "planned",
  "copying",
  "verifying",
  "destination_complete",
  "deleting_source",
] as const;
export type OperationPhase = (typeof operationPhases)[number];

const pathSchema = z.array(z.string().min(1)).min(1);

const operationSchema = z.object({
  id: z.uuid(),
  type: z.enum(operationTypes),
  phase: z.enum(operationPhases),
  sourcePath: pathSchema,
  destinationPath: pathSchema,
  createdAt: z.iso.datetime(),
});

export const operationJournalSchema = z.object({
  format: z.literal("shotboard-operations"),
  schemaVersion: z.literal(CURRENT_OPERATION_SCHEMA_VERSION),
  operations: z.array(operationSchema).max(1),
});

export type WorkspaceOperation = z.infer<typeof operationSchema>;
export type OperationJournal = z.infer<typeof operationJournalSchema>;

export type OperationJournalParseResult =
  | { readonly kind: "valid"; readonly journal: OperationJournal }
  | { readonly kind: "newer"; readonly schemaVersion: number; readonly raw: string }
  | { readonly kind: "invalid"; readonly reason: string; readonly raw: string };

const headerSchema = z.object({
  format: z.literal("shotboard-operations"),
  schemaVersion: z.number().int().nonnegative(),
});

/** Parses and path-validates an untrusted portable operation journal. */
export function parseOperationJournal(raw: string): OperationJournalParseResult {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return { kind: "invalid", reason: "operations.json is not valid JSON.", raw };
  }
  const header = headerSchema.safeParse(value);
  if (!header.success) {
    return { kind: "invalid", reason: "operations.json has an invalid format header.", raw };
  }
  if (header.data.schemaVersion > CURRENT_OPERATION_SCHEMA_VERSION) {
    return { kind: "newer", schemaVersion: header.data.schemaVersion, raw };
  }
  const journal = operationJournalSchema.safeParse(value);
  if (!journal.success) {
    return { kind: "invalid", reason: z.prettifyError(journal.error), raw };
  }
  try {
    for (const operation of journal.data.operations) {
      normalizeRelativePath(operation.sourcePath);
      normalizeRelativePath(operation.destinationPath);
    }
  } catch (error) {
    return {
      kind: "invalid",
      reason: String(error),
      raw,
    };
  }
  return { kind: "valid", journal: journal.data };
}

/** Serializes one validated journal with stable human-readable indentation. */
export function serializeOperationJournal(journal: OperationJournal): string {
  return `${JSON.stringify(operationJournalSchema.parse(journal), null, 2)}\n`;
}
